// The app's keyboard, for components: register a scope for a component's
// life, mark a text field's keys, open the key guide, carry out what an
// `update` returned, and read what the keyboard is waiting for.

import { untrack } from 'svelte';
import type { Attachment } from 'svelte/attachments';
import type { Command } from '../commands/command';
import { outcomeOf, type Outcome, type Shared, type Update } from '../commands/effects';
import { port, Program } from '../runtime/effects';
import { toState } from '../runtime/state.svelte';
import { guide, type GuideSection } from './guide';
import type { Pending } from './parser';
import type { Notice } from './program';
import { keysFor, SCOPES, type ScopeName } from './bindings';
import { Router, type GuideSpec, type Handle, type Registration } from './router';

/**
 * A scope a component registers, by its name in the table (`SCOPES` in
 * keys/bindings.ts says its kind and the sections live while it is), with
 * the `handle` that answers its commands.
 */
export interface ScopeSpec {
  scope: ScopeName;
  handle: Handle;
  /** Where the focus goes when Esc leaves a text field under this scope (the innermost scope's home wins). */
  home?: () => HTMLElement | null | undefined;
  /** For a text field's scope: the element whose keys it takes. */
  within?: Element;
  guide?: GuideSpec;
}

const resolved = ({ scope, ...rest }: ScopeSpec) => ({ kind: SCOPES[scope].kind, sections: SCOPES[scope].sections, ...rest });

/** What the key guide shows over the current page. */
export interface GuideView {
  heading: string;
  intro: string;
  sections: GuideSection[];
}

class Keyboard {
  #router: Router;
  #version = $state(0);
  #scopes = 0;
  #detach: (() => void) | null = null;
  /** The embedded frame that has the focus, so the page's shortcuts are paused (its keys never reach this document); null when none has. */
  frame = $state<Element | null>(null);
  /** Whether the key guide is open. */
  guideOpen = $state(false);

  constructor() {
    this.#router = new Router({
      onChange: () =>
        untrack(() => {
          this.#version++;
          this.frame = this.#router.frame;
        }),
    });
  }

  /** Registers a scope (see `Registration`). The window is listened to while any scope is registered. */
  use(spec: ScopeSpec): (() => void) & { update(spec: ScopeSpec): void } {
    if (this.#scopes++ === 0 && typeof window !== 'undefined') this.#detach = this.#router.attach(window);
    const reg = this.#router.push(resolved(spec));
    let done = false;
    const off = () => {
      if (done) return;
      done = true;
      reg();
      if (--this.#scopes === 0) {
        this.#detach?.();
        this.#detach = null;
      }
    };
    return Object.assign(off, { update: (next: ScopeSpec) => void (done || reg.update(resolved(next))) });
  }

  showGuide() {
    this.guideOpen = true;
  }

  closeGuide() {
    this.guideOpen = false;
  }

  /** Carries `command` out as its key would, in the scopes the page has now: for a control that does what a key does. */
  run(command: Command): void {
    this.#router.run(command);
  }

  /** Say that a command has nothing to act on here (see `Router.notApplicable`). */
  notApplicable(why: string, instead?: Command): void {
    this.#router.notApplicable(why, instead);
  }

  /**
   * The key to tell a person for `command` on this page: the shortest that
   * reaches it (a kept alias before the grammar's longer form), as written
   * for a person; null when none does.
   */
  key(command: Command): string | null {
    void this.#version;
    const keys = this.#router.keysFor(command);
    return keys.reduce<string | null>((best, k) => (best === null || k.length < best.length ? k : best), null);
  }

  /** Every key that reaches `command` on this page, as written for a person: the grammar's form first. */
  keys(command: Command): string[] {
    void this.#version;
    return this.#router.keysFor(command);
  }

  /**
   * The keys that reach `command` while `scope` is innermost, as one phrase
   * for a field's placeholder ("⌘ Enter or Ctrl Enter"); null when none
   * does. For a scope that is live only while the focus is in it (a text
   * field's), whose keys the page as it is does not show.
   */
  keysIn(scope: ScopeName, command: Command): string | null {
    void this.#version;
    const keys = keysFor(this.#router.bindings, command, SCOPES[scope].sections);
    return keys.length ? keys.join(' or ') : null;
  }

  /** ` (key)` for a tooltip, or nothing when no key reaches `command` here. */
  hint(command: Command): string {
    const k = this.key(command);
    return k ? ` (${k})` : '';
  }

  /** Whether the page has anything `command` could act on (its guide's `offers`): true where the page does not say. */
  offers(command: Command): boolean {
    void this.#version;
    const spec = [...this.#router.active()].reverse().find((s) => s.guide)?.guide;
    return spec?.offers ? spec.offers(command) : true;
  }

  /** What is typed so far and what may follow, while the keyboard waits for the next key. */
  get pending(): Pending | null {
    void this.#version;
    return this.#router.pending;
  }

  /** What was last said about a key, while it shows. */
  get notice(): Notice | null {
    void this.#version;
    return this.#router.notice;
  }

  /**
   * The guide over the registered scopes, generated from the bindings
   * table; null when the page registered none with a guide. The guide's
   * words are those of the outermost scope that names one: the app's own
   * page scope names none, and is outermost whenever a page mounts after it.
   */
  get guide(): GuideView | null {
    void this.#version;
    const active = this.#router.active();
    const spec = [...active].reverse().find((s) => s.guide)?.guide;
    if (!spec) return null;
    return {
      heading: spec.heading,
      intro: spec.intro,
      sections: guide({ bindings: this.#router.bindings, live: active.flatMap((s) => s.sections), related: spec.related, controls: spec.controls, offers: spec.offers }),
    };
  }
}

export const keyboard = new Keyboard();

/**
 * Registers a scope for the life of the calling component (call it during
 * component setup). Pass a function to choose the spec from reactive state:
 * the scope's spec is replaced whenever what the function reads changes.
 * Only reads made while building the spec are tracked, not those `handle` makes.
 */
export function useKeys(spec: ScopeSpec | (() => ScopeSpec)): void {
  let reg: ReturnType<typeof keyboard.use> | null = null;
  // A new spec updates the registration in place, so the scope keeps its place among scopes of its kind.
  $effect(() => {
    const s = typeof spec === 'function' ? spec() : spec;
    untrack(() => {
      if (reg) reg.update(s);
      else reg = keyboard.use(s);
    });
  });
  $effect(() => () => {
    reg?.();
    reg = null;
  });
}

/**
 * Carries out what an `update` returned, in order, and says how the scope
 * answered the command (the value a scope's `handle` returns). The
 * effects every view shares are carried out here: a command with nothing
 * to act on is said through the keyboard's notice, and `announce` reads
 * out the rest. `perform` carries out the view's own; when it returns a
 * promise, the effects after it wait for it.
 */
export function carryOut<E extends { kind: string }>(
  effects: readonly (E | Shared)[],
  perform: (effect: E) => void | Promise<void>,
  announce: (text: string) => void = () => {},
): Outcome {
  const outcome = outcomeOf(effects);
  if (outcome !== 'handled') return outcome;
  const one = (e: E | Shared): void | Promise<void> => {
    switch (e.kind) {
      case 'not-applicable': {
        const n = e as Extract<Shared, { kind: 'not-applicable' }>;
        return keyboard.notApplicable(n.why, n.instead);
      }
      case 'announce':
        return announce((e as Extract<Shared, { kind: 'announce' }>).text);
      case 'pass':
      case 'leave':
        return;
      default:
        return perform(e as E);
    }
  };
  const from = (i: number): void => {
    for (; i < effects.length; i++) {
      const waited = one(effects[i]);
      if (waited) {
        const next = i + 1;
        void waited.then(
          () => from(next),
          (error) => console.error('Changeling: an effect failed; the ones after it are dropped.', error),
        );
        return;
      }
    }
  };
  from(0);
  return outcome;
}

/** A view's model, held by a runtime Program: read it reactively, change it only by sending. */
export interface ViewModel<S, Msg> {
  /** The model now; reading it in a component, `$derived` or `$effect` tracks it. */
  readonly state: S;
  /** Sends a message through the view's `update`; answers how the scope answered it (what a scope's `handle` returns). */
  send(msg: Msg): Outcome;
}

/**
 * A view's model as a runtime Program (runtime/effects.ts). The model is
 * what the view's `update` last returned and nothing else: a command, a
 * pointer, an answer to an earlier effect all reach it as messages, and
 * the view reads it back reactively. `env` is read when a message is
 * sent: the facts the update needs that the view does not own (what a
 * store or the page shows). The effects an update returns leave through
 * the Program's port and are carried out in order (`carryOut`).
 */
export function viewModel<S, Env, Msg, E extends { kind: string }>(o: {
  init: S;
  env: () => Env;
  update: (state: S, env: Env, msg: Msg) => Update<S, E>;
  perform: (effect: E) => void | Promise<void>;
  announce?: (text: string) => void;
}): ViewModel<S, Msg> {
  /** How the message being sent was answered; a message sent from inside an effect answers no key. */
  let awaited: { outcome: Outcome } | null = null;
  const program = new Program<S, { msg: Msg; env: Env }, readonly (E | Shared)[]>({
    init: () => [o.init],
    update: (state, m) => {
      const [next, effects] = o.update(state, m.env, m.msg);
      return [next, [port(effects)]];
    },
    port: (effects) => {
      const asked = awaited;
      awaited = null;
      const outcome = carryOut(effects, o.perform, o.announce);
      if (asked) asked.outcome = outcome;
    },
  });
  const view = toState(program);
  return {
    get state() {
      return view.current;
    },
    send(msg) {
      const asked = { outcome: 'handled' as Outcome };
      awaited = asked;
      program.send({ msg, env: untrack(o.env) });
      awaited = null;
      return asked.outcome;
    },
  };
}

/** What a comment box is asked to do. */
export type FieldCommand = 'submit' | 'cancel';

/**
 * A comment box's keys, as an attachment (`{@attach commentKeys(run)}` on
 * the box or its textarea): the keys that send it and close it. They run
 * before any other scope's, and never during IME composition. A `run`
 * that declines `cancel` (returns false) lets Esc leave the field as
 * anywhere.
 */
export function commentKeys(run: (command: FieldCommand) => boolean | void): Attachment<HTMLElement> {
  return commentBox(run).keys;
}

/**
 * A comment box's two commands, for its keys and its buttons alike:
 * `keys` is the attachment `commentKeys` gives, and `send` carries the
 * same command out for a button (Add draft sends `submit`, Cancel sends
 * `cancel`), so a key and the button beside it cannot come to differ.
 */
export function commentBox(run: (command: FieldCommand) => boolean | void): { keys: Attachment<HTMLElement>; send: (command: Extract<Command, { kind: 'submit' | 'cancel' }>) => void } {
  const handle = (c: Command) => (c.kind === 'submit' || c.kind === 'cancel' ? run(c.kind) : ('pass' as const));
  return { keys: (el) => keyboard.use({ scope: 'field', within: el, handle }), send: (command) => void handle(command) };
}
